/**
 * <cart-drawer-recommendations> — the cart drawer's "You may also like" slider.
 *
 * Products come from the Search & Discovery app's complementary products (Shopify product
 * recommendations endpoint, intent=complementary). This element lazy-fetches
 * sections/cart-drawer-recommendations.liquid once per unique product currently in the cart,
 * merges the responses, de-dupes by product id, drops anything already in the cart, then injects
 * the cards into the track.
 *
 * The cart drawer replaces this element wholesale on every cart change (assets/cart.js's
 * onCartUpdate, assets/cart-drawer.js's renderContents), so a fresh instance connects and
 * re-fetches automatically — no manual refresh wiring needed here.
 */
if (!customElements.get('cart-drawer-recommendations')) {
  customElements.define(
    'cart-drawer-recommendations',
    class CartDrawerRecommendations extends HTMLElement {
      connectedCallback() {
        this.inner = this.querySelector('.cart-recommendations__inner');
        this.track = this.querySelector('.cart-recommendations__track');
        this.dotsWrapper = this.querySelector('.cart-recommendations__dots');
        this.prevButton = this.querySelector('.cart-recommendations__arrow[data-dir="prev"]');
        this.nextButton = this.querySelector('.cart-recommendations__arrow[data-dir="next"]');
        if (!this.track || !this.inner) return;

        this.cartIds = (this.dataset.productIds || '')
          .split(',')
          .map((id) => id.trim())
          .filter(Boolean);
        if (!this.cartIds.length) return;

        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        // Lives inside the scrollable cart-drawer-items now, below however many line items and
        // frame-upsell cards happen to be above it — no longer a safe bet that it starts near
        // the visible viewport, so load eagerly instead of waiting for an IntersectionObserver
        // that may never fire until the shopper scrolls all the way down to it.
        this.load();
      }

      disconnectedCallback() {
        if (this.navResizeObserver) this.navResizeObserver.disconnect();
        if (this.track && this.onScroll) this.track.removeEventListener('scroll', this.onScroll);
        if (this.scrollAnimFrame) cancelAnimationFrame(this.scrollAnimFrame);
      }

      // Animates the track to an exact scrollLeft over a fixed, gentle duration — used for every
      // programmatic move (dots, arrows, swipe paging) instead of the browser's built-in
      // `scroll-behavior: smooth`, which is fast and not tunable.
      scrollTrackTo(left) {
        const track = this.track;
        const max = track.scrollWidth - track.clientWidth;
        const target = Math.max(0, Math.min(max, left));

        if (this.scrollAnimFrame) {
          cancelAnimationFrame(this.scrollAnimFrame);
          this.scrollAnimFrame = null;
        }

        if (this.reduceMotion) {
          track.scrollLeft = target;
          return;
        }

        const duration = 600;
        const start = track.scrollLeft;
        const distance = target - start;
        const startTime = performance.now();
        // Zero velocity at both ends — no jerk picking up from wherever the drag left off,
        // and no hard stop at the card boundary either.
        const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

        const step = (now) => {
          const progress = Math.min(1, (now - startTime) / duration);
          track.scrollLeft = start + distance * easeInOutCubic(progress);
          this.scrollAnimFrame = progress < 1 ? requestAnimationFrame(step) : null;
        };

        this.scrollAnimFrame = requestAnimationFrame(step);
      }

      async load() {
        if (this.loaded) return;
        this.loaded = true;

        const endpoint = this.dataset.endpoint;
        const sectionId = this.dataset.sectionId;
        const limit = this.dataset.limit || 6;
        let cards = [];

        try {
          const responses = await Promise.all(
            this.cartIds.map((id) => {
              const url =
                `${endpoint}?product_id=${encodeURIComponent(id)}` +
                `&intent=complementary&limit=${encodeURIComponent(limit)}` +
                `&section_id=${encodeURIComponent(sectionId)}`;
              return fetch(url)
                .then((response) => (response.ok ? response.text() : ''))
                .catch(() => '');
            })
          );

          const seen = {};
          this.cartIds.forEach((id) => {
            seen[id] = true;
          });

          responses.forEach((html) => {
            if (!html) return;
            const holder = document.createElement('div');
            holder.innerHTML = html;
            holder.querySelectorAll('.cart-rec-card').forEach((card) => {
              const pid = card.dataset.productId;
              if (!pid || seen[pid]) return;
              seen[pid] = true;
              cards.push(card);
            });
          });
        } catch (error) {
          cards = [];
        }

        cards = cards.slice(0, 12);
        if (!cards.length) {
          this.remove();
          return;
        }

        const out = document.createDocumentFragment();
        cards.forEach((card) => {
          card.setAttribute('role', 'listitem');
          out.appendChild(card);
        });
        this.track.appendChild(out);

        this.inner.hidden = false;
        this.setupDots(cards.length);
        this.setupNav();
        this.setupDrag();
      }

      setupDots(count) {
        if (!this.dotsWrapper || count < 2) return;

        this.dots = [];
        for (let i = 0; i < count; i += 1) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'cart-recommendations__dot';
          dot.setAttribute('aria-label', `${i + 1}`);
          dot.addEventListener('click', () => {
            const card = this.track.children[i];
            if (card) this.scrollTrackTo(card.offsetLeft);
          });
          this.dotsWrapper.appendChild(dot);
          this.dots.push(dot);
        }
        this.dots[0].classList.add('is-active');
      }

      // scrollBy() one card at a time, let the browser's own scroll-snap settle it.
      // Drag-to-scroll (mouse and touch) is handled separately by setupDrag().
      setupNav() {
        const cards = Array.prototype.slice.call(this.track.children);

        const pitch = () => {
          if (cards.length > 1) return cards[1].offsetLeft - cards[0].offsetLeft;
          return cards[0] ? cards[0].offsetWidth : this.track.clientWidth;
        };

        const pageBy = (direction) => {
          const step = pitch();
          if (!step) return;
          this.scrollTrackTo(this.track.scrollLeft + direction * step);
        };

        if (this.prevButton && this.nextButton) {
          this.prevButton.addEventListener('click', () => pageBy(-1));
          this.nextButton.addEventListener('click', () => pageBy(1));
        }

        const update = () => {
          const max = this.track.scrollWidth - this.track.clientWidth;
          if (this.prevButton) this.prevButton.disabled = this.track.scrollLeft <= 1;
          if (this.nextButton) this.nextButton.disabled = max <= 1 || this.track.scrollLeft >= max - 1;

          if (this.dots) {
            const step = pitch();
            const active = step ? Math.round(this.track.scrollLeft / step) : 0;
            this.dots.forEach((dot, index) => dot.classList.toggle('is-active', index === active));
          }
        };

        this.onScroll = () => {
          if (this.navFrame) return;
          this.navFrame = requestAnimationFrame(() => {
            this.navFrame = null;
            update();
          });
        };
        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        this.navResizeObserver = new ResizeObserver(update);
        this.navResizeObserver.observe(this.track);
        update();
      }

      // Click-and-drag scrolling via Pointer Events — one code path for mouse and touch alike.
      // Locks onto whichever axis the gesture starts moving in, so a mostly-vertical touch still
      // scrolls the page instead of getting hijacked by the carousel. The track follows the
      // pointer while dragging for feedback, but on release always settles on exactly one
      // neighbouring card (never several at once, no matter how far it was dragged) — a real
      // swipe (past a distance or velocity threshold) pages forward/back, anything shorter snaps
      // back to the card it started on.
      setupDrag() {
        const track = this.track;
        const DRAG_THRESHOLD = 6;
        const SWIPE_DISTANCE = 40;
        const SWIPE_VELOCITY = 0.35; // px/ms

        const pitch = () => {
          const cards = track.children;
          if (cards.length > 1) return cards[1].offsetLeft - cards[0].offsetLeft;
          return cards[0] ? cards[0].offsetWidth : track.clientWidth;
        };

        const goToIndex = (index) => {
          const max = track.children.length - 1;
          const clamped = Math.max(0, Math.min(max, index));
          const card = track.children[clamped];
          if (!card) return;
          this.scrollTrackTo(card.offsetLeft);
        };

        let pointerId = null;
        let startX = 0;
        let startY = 0;
        let startTime = 0;
        let scrollStart = 0;
        let axis = null; // 'x' | 'y' | null (undecided)
        let dragged = false;

        const onPointerDown = (event) => {
          if (event.button !== undefined && event.button !== 0) return;

          pointerId = event.pointerId;
          startX = event.clientX;
          startY = event.clientY;
          startTime = event.timeStamp;
          scrollStart = track.scrollLeft;
          axis = null;
          dragged = false;
        };

        const onPointerMove = (event) => {
          if (pointerId === null || event.pointerId !== pointerId) return;

          const dx = event.clientX - startX;
          const dy = event.clientY - startY;

          if (axis === null) {
            if (Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) return;
            axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
            if (axis === 'x') {
              track.setPointerCapture(pointerId);
              track.classList.add('is-dragging');
            }
          }

          if (axis !== 'x') return;

          dragged = true;
          event.preventDefault();
          track.scrollLeft = scrollStart - dx;
        };

        const endDrag = (event) => {
          if (pointerId === null || (event && event.pointerId !== pointerId)) return;
          if (axis === 'x' && track.hasPointerCapture(pointerId)) {
            track.releasePointerCapture(pointerId);
          }
          track.classList.remove('is-dragging');

          if (axis === 'x' && event) {
            const dx = event.clientX - startX;
            const elapsed = Math.max(1, event.timeStamp - startTime);
            const velocity = Math.abs(dx) / elapsed;
            const step = pitch();
            const startIndex = step ? Math.round(scrollStart / step) : 0;

            let targetIndex = startIndex;
            if (Math.abs(dx) > SWIPE_DISTANCE || velocity > SWIPE_VELOCITY) {
              targetIndex = dx < 0 ? startIndex + 1 : startIndex - 1;
            }
            goToIndex(targetIndex);
          }

          pointerId = null;
          axis = null;
        };

        track.addEventListener('pointerdown', onPointerDown);
        track.addEventListener('pointermove', onPointerMove);
        track.addEventListener('pointerup', endDrag);
        track.addEventListener('pointercancel', endDrag);

        // Swallow the click that follows a drag so it doesn't open the product or trigger add-to-cart.
        track.addEventListener(
          'click',
          (event) => {
            if (!dragged) return;
            dragged = false;
            event.preventDefault();
            event.stopPropagation();
          },
          true
        );
      }
    }
  );
}
